import './account-list.scss';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import Card from '../../../../components/layouts/card/card';
import TableRow from '../../../../components/table/components/table-row/table-row';
import Table from '../../../../components/table/table';
import AccountEditorStore from '../../stores/account-editor-store';
import accountListStore from '../../stores/account-list-store';
import AccountAddonsCard from "../../addons-section/account-addons-card";
import GridLineLayout from '../../../../components/layouts/grid-line-layout/grid-line-layout';
import portalStore from '../../../../stores/current-portal-store';
import { FEATURES } from '../../../../models/Plan';
import usePage from '../../../../components/layouts/base-page-layout/page-hook';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/inputs/button/button';
import { BUTTON_VARIANTS } from '../../../../components/inputs/button-variants';
import IconPlus from '../../../../components/icons/icon-plus/icon-plus';
import EditUserButton from '../edit-user-button/edit-user-button';
import { Tooltip } from '../../../../components/tooltip/tooltip';
import { IColumn } from '../../../../components/table/table.types';
import FontHeading2 from '../../../../components/fonts/font-heading2/font-heading2';
import IconBlock from '../../../../components/icons/icon-block/icon-block';
import IconUnblock from '../../../../components/icons/icon-unblock/icon-unblock';
import { ButtonIcon } from '../../../../components/inputs/button-icon/button-icon';

const Cell: React.FC<{ children: React.ReactNode, disabled?: boolean }> = ({ children, disabled = false }) => {
    return <span className={`account-list-column ${disabled && 'disabled'}`}>{children}</span>;
}


const AccountPagedList: React.FC = observer(() => {
    const { setSubTitles } = usePage();
    useEffect(() => {
        setSubTitles([]);
    }, [setSubTitles]);

    const navigate = useNavigate();
    const onCreateNewClicked = () => {
        const newId = accountListStore.setNewAccount();
        navigate({
            pathname: newId
        });
    }

    const onEditClick = (account: AccountEditorStore) => {
        if (account.id) {
            navigate({
                pathname: account.id.toString(),
            });
        }
    }

    const onEnableClick = (account: AccountEditorStore) => {
        account.toggleIsEnabled();
    }

    /** temporary allowing adding and modifying account only to sysadmins */
    const isActionsAllowed = true; 

    const accountsLabel = accountListStore.quota > 0
        ? `(${accountListStore.getActiveAccountsTotal()} out of ${accountListStore.quota})`
        : '';

    return (
        <GridLineLayout>
            <Card>
                <FontHeading2>{`Store users ${accountsLabel}`}</FontHeading2>
                <Table
                    isLoading={accountListStore.isLoading}

                    columns={[
                        {
                            content: '#',
                            sortBy: 'store_user_id',
                            sortable: true,
                            width: {
                                value: 0.2,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Email',
                            width: {
                                value: 2,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Name',
                            width: {
                                value: 1.2,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Last Name',
                            width: {
                                value: 1.2,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Role',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Permissions',
                            width: {
                                value: 2,
                                type: 'fr'
                            }
                        },
                        ...(isActionsAllowed ? [{
                            content: 'Actions',
                            width: {
                                value: 65,
                                type: 'px'
                            }
                        } as IColumn] : []),
                    ]} totalResultsTitle='User'>
                    {accountListStore.list.map((account: AccountEditorStore) => (
                        <TableRow
                            key={account.id}
                            cells={[
                                <Cell disabled={!account.is_enabled}>{account.store_user_id}</Cell>,
                                <Cell disabled={!account.is_enabled}>{account.email}</Cell>,
                                <Cell disabled={!account.is_enabled}>{account.first_name}</Cell>,
                                <Cell disabled={!account.is_enabled}>{account.surname}</Cell>,
                                <Cell disabled={!account.is_enabled}>{account.getRoleNames()}</Cell>,
                                <Cell disabled={!account.is_enabled}>{account.getPermissionNames()}</Cell>,

                                ...(isActionsAllowed ? [<div className='account-action-list'>
                                    <Tooltip content={account.is_enabled ? 'Disable account' : 'Enable account'}>
                                        {account.is_enabled ? 
                                            <ButtonIcon 
                                                disabled={!account.canUpdateAccountSettings || account.isParentOwnerUser}
                                                Component={IconBlock} 
                                                onClick={() => onEnableClick(account)} 
                                            /> :
                                            <ButtonIcon 
                                                disabled={!account.canUpdateAccountSettings || !accountListStore.canEnable} 
                                                Component={IconUnblock} 
                                                onClick={() => onEnableClick(account)} 
                                            />
                                        }
                                    </Tooltip>
                                    <EditUserButton disabled={account.isParentOwnerUser} onClick={() => onEditClick(account)} />
                                </div>] : [])
                            ]}
                        />
                    ))}
                </Table>
                {isActionsAllowed && <div>
                    <Button
                        variant={BUTTON_VARIANTS.Text}
                        text="add user"
                        leftIcon={IconPlus}
                        onClick={onCreateNewClicked}
                    />
                </div>}
            </Card>
            {portalStore.isFeatureLoaded([FEATURES.ADDONS]) && portalStore.isFeatureAllowed(FEATURES.ADDONS) &&
                <AccountAddonsCard />
            }
        </GridLineLayout>
    );
});

export default AccountPagedList;